import { create } from "zustand";
import { api, configureAuth } from "./api";
import { queryClient } from "./queryClient";
import type { Profile } from "./types";

/**
 * The session lives in memory: the access token never touches storage. A page
 * load restores it from the HttpOnly refresh cookie (Path=/api/v1/auth).
 */
type SessionState = {
  status: "loading" | "anonymous" | "signedIn";
  accessToken: string | null;
  profile: Profile | null;
};

export const useSession = create<SessionState>(() => ({ status: "loading", accessToken: null, profile: null }));

const signedOut = () => {
  useSession.setState({ status: "anonymous", accessToken: null, profile: null });
  queryClient.clear();
};

// One refresh at a time: the refresh token rotates, so two concurrent refreshes would sign the user out.
let refreshing: Promise<string | null> | null = null;
export function refreshAccessToken(): Promise<string | null> {
  refreshing ??= fetch("/api/v1/auth/refresh", { method: "POST", credentials: "same-origin" })
    .then(async (res) => {
      if (!res.ok) return null;
      const json = (await res.json()) as { data: { accessToken: string } };
      useSession.setState({ accessToken: json.data.accessToken });
      return json.data.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

configureAuth({ token: () => useSession.getState().accessToken, refresh: refreshAccessToken, signedOut });

export async function loadProfile(): Promise<Profile> {
  const profile = await api.get<Profile>("/auth/me");
  useSession.setState({ profile, status: "signedIn" });
  return profile;
}

export async function bootstrap(): Promise<void> {
  const token = await refreshAccessToken();
  if (!token) return signedOut();
  try {
    await loadProfile();
  } catch {
    signedOut();
  }
}

export async function signIn(email: string, password: string) {
  const { accessToken } = await api.post<{ accessToken: string }>("/auth/login", { email, password });
  useSession.setState({ accessToken });
  await loadProfile();
}

export async function signUp(input: { name: string; orgName?: string; email: string; password: string }) {
  const { accessToken } = await api.post<{ accessToken: string }>("/auth/signup", input);
  useSession.setState({ accessToken });
  await loadProfile();
}

export async function signOut() {
  try {
    await api.post("/auth/logout");
  } catch {
    // Leaving must always work; the refresh cookie is cleared server-side when reachable.
  }
  signedOut();
}

export async function switchOrg(orgId: string) {
  const { accessToken } = await api.post<{ accessToken: string }>("/orgs/switch", { orgId });
  useSession.setState({ accessToken });
  queryClient.clear();
  await loadProfile();
}
